import { useState, useEffect } from "react";
import { Search, Filter, MoreHorizontal, Edit, Settings, ArrowLeft, Save } from "lucide-react";
import { Button } from "./ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";

interface PaymentSetting {
  id: string;
  userRole: "SALES_AGENT" | "ATHLETE" | "COACH";
  firstTierReferrerPercentage: number;
  firstTierDuration: number;
  firstTierDurationUnit: "days" | "weeks" | "months" | "years";
  secondTierReferrerPercentage: number;
  secondTierDuration: number;
  secondTierDurationUnit: "days" | "weeks" | "months" | "years";
  thirdTierReferrerPercentage: number;
  thirdTierDuration: number;
  thirdTierDurationUnit: "days" | "weeks" | "months" | "years";
  applicationFeePercentage: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface EditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  setting: PaymentSetting | null;
  onSave: (settingId: string, data: Partial<PaymentSetting>) => void;
}

const durationUnits = [
  { value: "days", label: "Days" },
  { value: "weeks", label: "Weeks" },
  { value: "months", label: "Months" },
  { value: "years", label: "Years" }
];

interface PaymentEditFormProps {
  setting: PaymentSetting;
  onSave: (data: Partial<PaymentSetting>) => void;
  onCancel: () => void;
  isSaving: boolean;
}

function PaymentEditForm({ setting, onSave, onCancel, isSaving }: PaymentEditFormProps) {
  const [formData, setFormData] = useState({
    firstTierReferrerPercentage: 0,
    firstTierDuration: 0,
    firstTierDurationUnit: "months" as const,
    secondTierReferrerPercentage: 0,
    secondTierDuration: 0,
    secondTierDurationUnit: "months" as const,
    thirdTierReferrerPercentage: 0,
    thirdTierDuration: 0,
    thirdTierDurationUnit: "months" as const,
    applicationFeePercentage: 0
  });

  useEffect(() => {
    setFormData({
      firstTierReferrerPercentage: setting.firstTierReferrerPercentage,
      firstTierDuration: setting.firstTierDuration,
      firstTierDurationUnit: setting.firstTierDurationUnit,
      secondTierReferrerPercentage: setting.secondTierReferrerPercentage,
      secondTierDuration: setting.secondTierDuration,
      secondTierDurationUnit: setting.secondTierDurationUnit,
      thirdTierReferrerPercentage: setting.thirdTierReferrerPercentage,
      thirdTierDuration: setting.thirdTierDuration,
      thirdTierDurationUnit: setting.thirdTierDurationUnit,
      applicationFeePercentage: setting.applicationFeePercentage
    });
  }, [setting]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* User Role - Read Only */}
      <div>
        <label className="text-sm mb-2 block">User Role *</label>
        <Select value={setting.userRole} disabled>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="SALES_AGENT">SALES AGENT</SelectItem>
            <SelectItem value="ATHLETE">ATHLETE</SelectItem>
            <SelectItem value="COACH">COACH</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* First Tier Referrer Percentage */}
      <div>
        <label className="text-sm mb-2 block">First Tier Referrer Percentage</label>
        <Input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={formData.firstTierReferrerPercentage}
          onChange={(e) => setFormData(prev => ({ 
            ...prev, 
            firstTierReferrerPercentage: parseFloat(e.target.value) || 0 
          }))}
          required
        />
      </div>

      {/* Second Tier Referrer Percentage */}
      <div>
        <label className="text-sm mb-2 block">Second Tier Referrer Percentage</label>
        <Input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={formData.secondTierReferrerPercentage}
          onChange={(e) => setFormData(prev => ({ 
            ...prev, 
            secondTierReferrerPercentage: parseFloat(e.target.value) || 0 
          }))}
          required
        />
      </div>

      {/* Third Tier Referrer Percentage */}
      <div>
        <label className="text-sm mb-2 block">Third Tier Referrer Percentage</label>
        <Input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={formData.thirdTierReferrerPercentage}
          onChange={(e) => setFormData(prev => ({ 
            ...prev, 
            thirdTierReferrerPercentage: parseFloat(e.target.value) || 0 
          }))}
        />
      </div>

      {/* Application Fee Percentage */}
      <div>
        <label className="text-sm mb-2 block">Application Fee Percentage</label>
        <Input
          type="number"
          min="0"
          max="100"
          step="0.1"
          value={formData.applicationFeePercentage}
          onChange={(e) => setFormData(prev => ({ 
            ...prev, 
            applicationFeePercentage: parseFloat(e.target.value) || 0 
          }))}
          required
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-4">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button 
          type="submit" 
          disabled={isSaving}
          className="bg-blue-500 hover:bg-blue-600 text-white min-w-[100px]"
        >
          {isSaving ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </form>
  );
}

function EditDialog({ isOpen, onClose, setting, onSave }: EditDialogProps) {
  const [formData, setFormData] = useState({
    firstTierReferrerPercentage: 0,
    firstTierDuration: 0,
    firstTierDurationUnit: "months" as const,
    secondTierReferrerPercentage: 0,
    secondTierDuration: 0,
    secondTierDurationUnit: "months" as const,
    thirdTierReferrerPercentage: 0,
    thirdTierDuration: 0,
    thirdTierDurationUnit: "months" as const,
    applicationFeePercentage: 0
  });

  useEffect(() => {
    if (setting) {
      setFormData({
        firstTierReferrerPercentage: setting.firstTierReferrerPercentage,
        firstTierDuration: setting.firstTierDuration,
        firstTierDurationUnit: setting.firstTierDurationUnit,
        secondTierReferrerPercentage: setting.secondTierReferrerPercentage,
        secondTierDuration: setting.secondTierDuration,
        secondTierDurationUnit: setting.secondTierDurationUnit,
        thirdTierReferrerPercentage: setting.thirdTierReferrerPercentage,
        thirdTierDuration: setting.thirdTierDuration,
        thirdTierDurationUnit: setting.thirdTierDurationUnit,
        applicationFeePercentage: setting.applicationFeePercentage
      });
    }
  }, [setting]);

  const handleSave = () => {
    if (setting) {
      onSave(setting.id, formData);
    }
    onClose();
  };

  if (!setting) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            Edit Payment Settings
          </DialogTitle>
          <DialogDescription>
            Update referral percentages and durations for {setting.userRole.replace('_', ' ').toLowerCase()}
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-6">
          {/* User Role - Read Only */}
          <div>
            <label className="text-sm mb-2 block">User Role</label>
            <Select value={setting.userRole} disabled>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="SALES_AGENT">SALES AGENT</SelectItem>
                <SelectItem value="ATHLETE">ATHLETE</SelectItem>
                <SelectItem value="COACH">COACH</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* First Tier Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">First Tier Referrer</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm mb-2 block">Percentage</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.firstTierReferrerPercentage}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    firstTierReferrerPercentage: parseFloat(e.target.value) || 0 
                  }))}
                />
              </div>
              <div>
                <label className="text-sm mb-2 block">Duration</label>
                <Input
                  type="number"
                  min="0"
                  value={formData.firstTierDuration}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    firstTierDuration: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
              <div>
                <label className="text-sm mb-2 block">Duration Unit</label>
                <Select 
                  value={formData.firstTierDurationUnit} 
                  onValueChange={(value: any) => setFormData(prev => ({ 
                    ...prev, 
                    firstTierDurationUnit: value 
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {durationUnits.map(unit => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Second Tier Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">Second Tier Referrer</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm mb-2 block">Percentage</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.secondTierReferrerPercentage}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    secondTierReferrerPercentage: parseFloat(e.target.value) || 0 
                  }))}
                />
              </div>
              <div>
                <label className="text-sm mb-2 block">Duration</label>
                <Input
                  type="number"
                  min="0"
                  value={formData.secondTierDuration}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    secondTierDuration: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
              <div>
                <label className="text-sm mb-2 block">Duration Unit</label>
                <Select 
                  value={formData.secondTierDurationUnit} 
                  onValueChange={(value: any) => setFormData(prev => ({ 
                    ...prev, 
                    secondTierDurationUnit: value 
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {durationUnits.map(unit => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Third Tier Settings */}
          <div className="space-y-4">
            <h4 className="font-medium">Third Tier Referrer</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-sm mb-2 block">Percentage</label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.thirdTierReferrerPercentage}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    thirdTierReferrerPercentage: parseFloat(e.target.value) || 0 
                  }))}
                />
              </div>
              <div>
                <label className="text-sm mb-2 block">Duration</label>
                <Input
                  type="number"
                  min="0"
                  value={formData.thirdTierDuration}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    thirdTierDuration: parseInt(e.target.value) || 0 
                  }))}
                />
              </div>
              <div>
                <label className="text-sm mb-2 block">Duration Unit</label>
                <Select 
                  value={formData.thirdTierDurationUnit} 
                  onValueChange={(value: any) => setFormData(prev => ({ 
                    ...prev, 
                    thirdTierDurationUnit: value 
                  }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {durationUnits.map(unit => (
                      <SelectItem key={unit.value} value={unit.value}>
                        {unit.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Application Fee Percentage */}
          <div>
            <label className="text-sm mb-2 block">Application Fee Percentage</label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={formData.applicationFeePercentage}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                applicationFeePercentage: parseFloat(e.target.value) || 0 
              }))}
            />
          </div>
          
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentSettings() {
  const [settings, setSettings] = useState<PaymentSetting[]>([]);
  const [filteredSettings, setFilteredSettings] = useState<PaymentSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [currentView, setCurrentView] = useState<"list" | "edit">("list");
  const [selectedSetting, setSelectedSetting] = useState<PaymentSetting | null>(null);
  const [editDialog, setEditDialog] = useState<{
    isOpen: boolean;
    setting: PaymentSetting | null;
  }>({
    isOpen: false,
    setting: null
  });

  // Filter states
  const [searchUserRole, setSearchUserRole] = useState("");
  const [selectedUserRole, setSelectedUserRole] = useState<string>("all");
  const [minFirstTier, setMinFirstTier] = useState("");
  const [maxFirstTier, setMaxFirstTier] = useState("");

  // Mock payment settings data
  const mockSettings: PaymentSetting[] = [
    {
      id: "1",
      userRole: "SALES_AGENT",
      firstTierReferrerPercentage: 2.5,
      firstTierDuration: 3,
      firstTierDurationUnit: "months",
      secondTierReferrerPercentage: 2.5,
      secondTierDuration: 2,
      secondTierDurationUnit: "months",
      thirdTierReferrerPercentage: 0,
      thirdTierDuration: 0,
      thirdTierDurationUnit: "months",
      applicationFeePercentage: 30,
      active: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "2",
      userRole: "ATHLETE",
      firstTierReferrerPercentage: 2.5,
      firstTierDuration: 6,
      firstTierDurationUnit: "months",
      secondTierReferrerPercentage: 2.5,
      secondTierDuration: 3,
      secondTierDurationUnit: "months",
      thirdTierReferrerPercentage: 1.0,
      thirdTierDuration: 1,
      thirdTierDurationUnit: "months",
      applicationFeePercentage: 25,
      active: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    },
    {
      id: "3",
      userRole: "COACH",
      firstTierReferrerPercentage: 3.0,
      firstTierDuration: 1,
      firstTierDurationUnit: "years",
      secondTierReferrerPercentage: 2.0,
      secondTierDuration: 6,
      secondTierDurationUnit: "months",
      thirdTierReferrerPercentage: 1.0,
      thirdTierDuration: 3,
      thirdTierDurationUnit: "months",
      applicationFeePercentage: 20,
      active: true,
      createdAt: "2024-01-01T00:00:00Z",
      updatedAt: "2024-01-01T00:00:00Z"
    }
  ];

  // Simulate API call to fetch payment settings
  useEffect(() => {
    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        // Simulate API call - replace with real API endpoint
        // const response = await fetch("host/api/v1/payment-settings", {
        //   headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
        // });
        
        // Mock data for demonstration
        await new Promise(resolve => setTimeout(resolve, 800));
        
        setSettings(mockSettings);
        setFilteredSettings(mockSettings);
      } catch (error) {
        console.error("Failed to fetch payment settings:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = [...settings];

    // User role search
    if (searchUserRole) {
      filtered = filtered.filter(setting => 
        setting.userRole.toLowerCase().includes(searchUserRole.toLowerCase())
      );
    }

    // User role filter
    if (selectedUserRole !== "all") {
      filtered = filtered.filter(setting => setting.userRole === selectedUserRole);
    }

    // Application fee percentage filter
    if (minFirstTier) {
      filtered = filtered.filter(setting => setting.applicationFeePercentage >= parseFloat(minFirstTier));
    }
    if (maxFirstTier) {
      filtered = filtered.filter(setting => setting.applicationFeePercentage <= parseFloat(maxFirstTier));
    }

    setFilteredSettings(filtered);
  }, [settings, searchUserRole, selectedUserRole, minFirstTier, maxFirstTier]);

  const clearFilters = () => {
    setSearchUserRole("");
    setSelectedUserRole("all");
    setMinFirstTier("");
    setMaxFirstTier("");
  };

  const handleEdit = (setting: PaymentSetting) => {
    setSelectedSetting(setting);
    setCurrentView("edit");
  };

  const handleEditModal = (setting: PaymentSetting) => {
    setEditDialog({
      isOpen: true,
      setting
    });
  };

  const handleSave = async (settingId: string, data: Partial<PaymentSetting>) => {
    try {
      // Simulate API call to update setting
      // const response = await fetch(`host/api/v1/payment-settings/${settingId}`, {
      //   method: 'PUT',
      //   headers: {
      //     'Content-Type': 'application/json',
      //     Authorization: `Bearer ${localStorage.getItem("authToken")}`
      //   },
      //   body: JSON.stringify(data)
      // });

      console.log("Updating payment setting:", settingId, data);

      // Update local state
      setSettings(prev => prev.map(setting => 
        setting.id === settingId 
          ? { ...setting, ...data, updatedAt: new Date().toISOString() }
          : setting
      ));
    } catch (error) {
      console.error("Failed to update payment setting:", error);
    }
  };

  const handlePageSave = async (data: Partial<PaymentSetting>) => {
    if (!selectedSetting) return;
    
    setIsSaving(true);
    try {
      await handleSave(selectedSetting.id, data);
      setCurrentView("list");
      setSelectedSetting(null);
    } catch (error) {
      console.error("Failed to save payment setting:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const getUserRoleBadge = (userRole: string) => {
    switch (userRole) {
      case "SALES_AGENT":
        return <Badge variant="default">SALES AGENT</Badge>;
      case "ATHLETE":
        return <Badge variant="secondary">ATHLETE</Badge>;
      case "COACH":
        return <Badge variant="outline">COACH</Badge>;
      default:
        return <Badge variant="secondary">{userRole}</Badge>;
    }
  };

  // Full page edit view
  if (currentView === "edit" && selectedSetting) {
    return (
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => {
              setCurrentView("list");
              setSelectedSetting(null);
            }}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1>Edit</h1>
            <p className="text-muted-foreground">
              Update payment settings for {selectedSetting.userRole.replace('_', ' ').toLowerCase()}
            </p>
          </div>
        </div>

        {/* Edit Form */}
        <Card>
          <CardContent className="pt-6">
            <PaymentEditForm
              setting={selectedSetting}
              onSave={handlePageSave}
              onCancel={() => {
                setCurrentView("list");
                setSelectedSetting(null);
              }}
              isSaving={isSaving}
            />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="space-y-4">
          <div className="h-8 bg-muted animate-pulse rounded" />
          <div className="h-4 bg-muted animate-pulse rounded w-1/3" />
          <div className="space-y-2">
            {Array.from({ length: 5 }, (_, i) => (
              <div key={i} className="h-12 bg-muted animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1>List</h1>
              <Badge variant="secondary" className="rounded-full">
                {filteredSettings.length}
              </Badge>
            </div>
            <p className="text-muted-foreground">
              Manage payment and referral settings by user role
            </p>
          </div>
        </div>
        <Button variant="outline">
          <Filter className="h-4 w-4 mr-2" />
          Filter
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Settings</CardTitle>
              <CardDescription>
                Configure referral percentages for different user roles
              </CardDescription>
            </div>
            <Button variant="outline" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div>
              <label className="text-sm mb-2 block">Search User Role</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search roles..."
                  value={searchUserRole}
                  onChange={(e) => setSearchUserRole(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>

            <div>
              <label className="text-sm mb-2 block">User Role</label>
              <Select value={selectedUserRole} onValueChange={setSelectedUserRole}>
                <SelectTrigger>
                  <SelectValue placeholder="All Roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="SALES_AGENT">Sales Agent</SelectItem>
                  <SelectItem value="ATHLETE">Athlete</SelectItem>
                  <SelectItem value="COACH">Coach</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm mb-2 block">Min App Fee %</label>
              <Input
                type="number"
                placeholder="0"
                value={minFirstTier}
                onChange={(e) => setMinFirstTier(e.target.value)}
              />
            </div>

            <div>
              <label className="text-sm mb-2 block">Max App Fee %</label>
              <Input
                type="number"
                placeholder="100"
                value={maxFirstTier}
                onChange={(e) => setMaxFirstTier(e.target.value)}
              />
            </div>
          </div>

          {/* Results Count */}
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-muted-foreground">
              Showing {filteredSettings.length} of {settings.length} payment settings
            </p>
          </div>

          {/* Payment Settings Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User Role</TableHead>
                  <TableHead>First Tier</TableHead>
                  <TableHead>Second Tier</TableHead>
                  <TableHead>Third Tier</TableHead>
                  <TableHead>App Fee</TableHead>
                  <TableHead className="w-[50px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSettings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="text-muted-foreground">
                        <Search className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No payment settings found matching your filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredSettings.map((setting) => (
                    <TableRow key={setting.id}>
                      <TableCell>
                        {getUserRoleBadge(setting.userRole)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{setting.firstTierReferrerPercentage}%</div>
                          <div className="text-xs text-muted-foreground">
                            {setting.firstTierDuration > 0 ? 
                              `${setting.firstTierDuration} ${setting.firstTierDurationUnit}` : 
                              'No duration'
                            }
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{setting.secondTierReferrerPercentage}%</div>
                          <div className="text-xs text-muted-foreground">
                            {setting.secondTierDuration > 0 ? 
                              `${setting.secondTierDuration} ${setting.secondTierDurationUnit}` : 
                              'No duration'
                            }
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className="font-medium">{setting.thirdTierReferrerPercentage}%</div>
                          <div className="text-xs text-muted-foreground">
                            {setting.thirdTierDuration > 0 ? 
                              `${setting.thirdTierDuration} ${setting.thirdTierDurationUnit}` : 
                              'No duration'
                            }
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">{setting.applicationFeePercentage}%</div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(setting)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit Settings
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditModal(setting)}>
                              <Edit className="h-4 w-4 mr-2" />
                              Quick Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              View History
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              Reset to Default
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <EditDialog
        isOpen={editDialog.isOpen}
        onClose={() => setEditDialog({ isOpen: false, setting: null })}
        setting={editDialog.setting}
        onSave={handleSave}
      />
    </div>
  );
}